import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, Download, Upload, X } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Colors, { withAlpha } from '../../constants/Colors';
import { gamesApi, GameImportResponse } from '../../services/api';

// Bulk "Add Game Record" from the .xlsx template. The backend (HomeStream-Admin-Backend
// services/game_import_service.py) resolves each row exactly like handleCreateGame in
// GamesPanel.tsx and creates the games unpublished. Any invalid row blocks the whole file.

function downloadBase64(filename: string, base64: string) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

interface GameImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

export default function GameImportModal({ open, onClose, onImported }: GameImportModalProps) {
  const [templateLoading, setTemplateLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<GameImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Start clean every time the popup opens.
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setResult(null);
    setError(null);
  }, [open]);

  // Closing mid-import would hide the outcome, so the popup stays put until it finishes.
  const handleClose = () => {
    if (importLoading) return;
    onClose();
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, importLoading]);

  if (!open) return null;

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    const response = await gamesApi.getImportTemplate();
    setTemplateLoading(false);
    if (response.success && response.data) {
      downloadBase64(response.data.filename, response.data.file_base64);
    } else {
      toast.error(response.error || 'Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImportLoading(true);
    setError(null);
    setResult(null);
    const response = await gamesApi.importGames(file);
    setImportLoading(false);

    if (!response.success || !response.data) {
      setError(response.error || 'Import failed');
      toast.error(response.error || 'Import failed');
      return;
    }

    setResult(response.data);
    if (response.data.created_count > 0) {
      onImported();
    }
    if (response.data.success) {
      toast.success(`Imported ${response.data.created_count} game(s) as unpublished`);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } else {
      toast.error(`${response.data.failed_count} row(s) need fixing`);
    }
  };

  return (
    <div className="seasons-modal-overlay" role="presentation" onClick={handleClose}>
      <div
        className="seasons-modal-panel settings-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Import games from Excel"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(640px, calc(100vw - 24px))' }}
      >
        <Card
          title="Import Games from Excel"
          description="Download the template, fill one game per row, then upload it. Teams, venues and bot IDs are matched the same way as Add Game, and imported games stay unpublished."
          headerAction={
            <button
              type="button"
              className="seasons-modal-close"
              onClick={handleClose}
              disabled={importLoading}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <Button variant="outline" className="btn-small" onClick={handleDownloadTemplate} loading={templateLoading}>
                <Download size={14} />
                Download template
              </Button>
            </div>
            <input
              ref={fileInputRef}
              className="form-input"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setResult(null);
                setError(null);
              }}
              disabled={importLoading}
            />
          </div>

          {error && (
            <div
              style={{
                marginTop: '14px',
                padding: '10px 12px',
                borderRadius: '10px',
                fontSize: '13px',
                color: Colors.error,
                background: withAlpha(Colors.error, 0.06),
                border: `1px solid ${withAlpha(Colors.error, 0.2)}`,
              }}
            >
              {error}
            </div>
          )}

          {result && (
            <div style={{ marginTop: '14px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: result.success ? '#15803d' : '#B45309',
                }}
              >
                {result.success ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                {result.success
                  ? `${result.created_count} of ${result.total_rows} game(s) created as unpublished.`
                  : result.created_count > 0
                    ? `${result.created_count} game(s) created; ${result.failed_count} row(s) failed to save.`
                    : `Nothing was imported: ${result.failed_count} of ${result.total_rows} row(s) have errors. Fix them and upload again.`}
              </div>

              {result.errors.length > 0 && (
                <div style={{ marginTop: '10px', maxHeight: '280px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: Colors.textLight }}>
                        <th style={{ padding: '6px 8px', width: '60px' }}>Row</th>
                        <th style={{ padding: '6px 8px' }}>Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((rowError) => (
                        <tr key={rowError.row} style={{ borderTop: `1px solid ${withAlpha(Colors.border, 0.14)}` }}>
                          <td style={{ padding: '6px 8px', verticalAlign: 'top', fontWeight: 600 }}>{rowError.row}</td>
                          <td style={{ padding: '6px 8px' }}>
                            {rowError.errors.map((message) => (
                              <div key={message}>{message}</div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div
            className="button-group"
            style={{ marginTop: '18px', paddingTop: '16px', borderTop: '1px solid rgba(var(--color-border-rgb), 0.14)', justifyContent: 'flex-end' }}
          >
            <Button variant="outline" className="btn-small" onClick={handleClose} disabled={importLoading}>
              {result?.success ? 'Done' : 'Cancel'}
            </Button>
            <Button variant="success" className="btn-small" onClick={handleImport} loading={importLoading} disabled={!file}>
              <Upload size={14} />
              Import
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
